// StackedBarGraph.tsx
import React from 'react';
import { BarChart } from '@mui/x-charts';
import { Typography } from '@mui/material';
import { colours } from 'utils/constants';
import Skeleton from '@mui/material/Skeleton';
import NoData from 'components/elements/Reports/NoData';

type TransformedData = {
  x: (string | number)[];
  series: {
    timeBookedAndOccupied: number[];
    timeNotBookedButOccupied: number[];
    timeBookedButNotOccupied: number[];
  };
};

interface StackedBarGraphProps {
  data: TransformedData | null;
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  yAxisMax?: number;
  isLoading: boolean;
}

const StackedBarGraph: React.FC<StackedBarGraphProps> = ({
  data,
  title,
  xAxisLabel,
  yAxisLabel,
  yAxisMax,
  isLoading
}) => {
  const GRAPH_HEIGHT = 400;
  if (!data) {
    return (
      <NoData height={GRAPH_HEIGHT} hint="No meetings were booked within the selected time span." />
    );
  }

  const { x, series } = data;

  const colors = {
    timeBookedAndOccupied: '#99d2d5', // Green
    timeNotBookedButOccupied: '#ffd400', // yellow
    timeBookedButNotOccupied: '#ff8071' // Red
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={GRAPH_HEIGHT} />;
  }

  return (
    <div style={{ width: '100%', height: '430px' }}>
      <Typography variant="h6" align="center">
        {title}
      </Typography>

      <BarChart
        sx={{
          '& .MuiChartsAxis-left .MuiChartsAxis-label': { transform: 'translate(-25px, 0)' },
          '& .MuiChartsAxis-bottom .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          '& .MuiChartsAxis-left .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          '& .MuiChartsAxis-right .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          '& .MuiChartsAxis-root .MuiChartsAxis-label': { fill: colours.axisGrey },
          '& .MuiChartsAxis-tick': { stroke: colours.axisGrey, strokeWidth: 0.4 },
          '& .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel': { fill: colours.axisGrey }
        }}
        xAxis={[
          {
            data: x,
            scaleType: 'band',
            label: xAxisLabel
          }
        ]}
        yAxis={[
          {
            label: yAxisLabel,
            max: yAxisMax
          }
        ]}
        series={[
          {
            data: series.timeBookedAndOccupied,
            label: 'Booked Meetings',
            stack: 'total',
            color: colors.timeBookedAndOccupied
          },
          {
            data: series.timeNotBookedButOccupied,
            label: 'Unbooked Meetings',
            stack: 'total',
            color: colors.timeNotBookedButOccupied
          },
          {
            data: series.timeBookedButNotOccupied,
            label: 'Booked meetings not attended',
            stack: 'total',
            color: colors.timeBookedButNotOccupied
          }
        ]}
        height={GRAPH_HEIGHT}
        margin={{ left: 80 }}
      />
    </div>
  );
};

export default StackedBarGraph;
